/**
 * RecordEditModal - universal edit popup for ANY admin table row.
 * Pass `record`, `fields`, `endpoint` and it builds a form + saves on click.
 *
 * Usage:
 *   <RecordEditModal
 *     open={true}
 *     onClose={...}
 *     record={user}
 *     title="Edit User"
 *     endpoint="/admin/users/usr_xxx"
 *     fields={[
 *       { name: "display_name", label: "Display Name", type: "text" },
 *       { name: "email", label: "Email", type: "email" },
 *       { name: "is_pilot", label: "Pilot tier", type: "boolean" },
 *       { name: "plan", label: "Plan", type: "select", options: [...] },
 *       { name: "org_id", label: "Organization", type: "ref", refEndpoint: "/admin/organizations" },
 *     ]}
 *     onSaved={(updated) => ...}
 *   />
 */
import { useState, useEffect } from "react";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const headers = () => {
  const t = localStorage.getItem("session_token");
  return t ? { "Content-Type": "application/json", Authorization: `Bearer ${t}` } : { "Content-Type": "application/json" };
};

export default function RecordEditModal({ open, onClose, record, title, endpoint, fields, onSaved, method = "PATCH" }) {
  const [data, setData] = useState({});
  const [refs, setRefs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && record) {
      const init = {};
      fields.forEach(f => { init[f.name] = record[f.name] ?? f.default ?? ""; });
      setData(init);
    }
  }, [open, record, fields]);

  // Lazy load reference dropdown options
  useEffect(() => {
    if (!open) return;
    fields.filter(f => f.type === "ref" && f.refEndpoint).forEach(async f => {
      try {
        const r = await fetch(`${API}${f.refEndpoint}`, { headers: headers() });
        if (r.ok) {
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.organizations || d.results || d.data || []);
          setRefs(p => ({ ...p, [f.name]: list }));
        }
      } catch {}
    });
  }, [open, fields]);

  const setField = (name, value) => setData(p => ({ ...p, [name]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}${endpoint}`, { method, headers: headers(), body: JSON.stringify(data) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast.success("Saved");
      onSaved?.(j);
      onClose?.();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="record-edit-modal">
        <DialogHeader><DialogTitle>{title || "Edit"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {fields.map(f => (
            <div key={f.name} className="space-y-1.5">
              <Label className="text-xs">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              {f.type === "boolean" ? (
                <div className="flex items-center gap-2">
                  <Switch checked={!!data[f.name]} onCheckedChange={(v) => setField(f.name, v)} data-testid={`edit-${f.name}`} />
                  <span className="text-xs text-gray-500">{data[f.name] ? "Yes" : "No"}</span>
                </div>
              ) : f.type === "select" ? (
                <select value={data[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)}
                  className="w-full h-9 text-xs border border-gray-200 rounded-md px-2 bg-white" data-testid={`edit-${f.name}`}>
                  <option value="">- none -</option>
                  {(f.options || []).map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                </select>
              ) : f.type === "ref" ? (
                <select value={data[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)}
                  className="w-full h-9 text-xs border border-gray-200 rounded-md px-2 bg-white" data-testid={`edit-${f.name}`}>
                  <option value="">- unlinked -</option>
                  {(refs[f.name] || []).map(o => <option key={o[f.refKey || "org_id"]} value={o[f.refKey || "org_id"]}>{o[f.refLabel || "name"] || o[f.refKey || "org_id"]}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <Textarea value={data[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} rows={3} className="text-xs" data-testid={`edit-${f.name}`} />
              ) : (
                <Input type={f.type || "text"} value={data[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="h-9 text-xs" data-testid={`edit-${f.name}`} />
              )}
              {f.help && <p className="text-[10px] text-gray-400">{f.help}</p>}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="record-edit-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="record-edit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
